import { useState, useEffect } from "react";
import { useSavedDescriptions } from "../../state/db/savedDescriptions";

// Reusable position types
export type ContentPosition = "INLINE" | "START" | "END" | "USE_TEMPLATE";
export type BasicPosition = "START" | "END" | "INLINE";

// Generic position record type
export type PositionRecord<T extends string> = Record<number, T>;

// Generic position handler type
export type PositionHandler<T extends string> = (
  id: number,
  position: T
) => void;

// Specific position states
export interface PositionStates {
  content: PositionRecord<ContentPosition>;
  hashtag: PositionRecord<BasicPosition>;
  tag: PositionRecord<BasicPosition>;
}

// Specific position handlers
export interface PositionHandlers {
  content: PositionHandler<ContentPosition>;
  hashtag: PositionHandler<BasicPosition>;
  tag: PositionHandler<BasicPosition>;
}

export function usePositionManagement(selectedSavedDescriptions: number[]) {
  const { savedDescriptions } = useSavedDescriptions();
  // Local state to track position for each saved description (not saved to database)
  const [savedDescriptionPositions, setSavedDescriptionPositions] = useState<
    PositionRecord<ContentPosition>
  >({});
  const [
    savedDescriptionHashtagPositions,
    setSavedDescriptionHashtagPositions,
  ] = useState<PositionRecord<BasicPosition>>({});
  const [savedDescriptionTagPositions, setSavedDescriptionTagPositions] =
    useState<PositionRecord<BasicPosition>>({});

  useEffect(() => {
    setSavedDescriptionPositions((prev) => {
      const newPositions: PositionRecord<ContentPosition> = {};
      savedDescriptions.forEach(({ id, position }) => {
        // Keep existing position or set default
        newPositions[id] = position || prev[id] || "INLINE";
      });
      return newPositions;
    });

    setSavedDescriptionHashtagPositions((prev) => {
      const newPositions: PositionRecord<BasicPosition> = {};
      savedDescriptions.forEach(({ id, hashtag_position }) => {
        // Keep existing position or set default
        newPositions[id] = hashtag_position || prev[id] || "START";
      });
      return newPositions;
    });

    setSavedDescriptionTagPositions((prev) => {
      const newPositions: PositionRecord<BasicPosition> = {};
      savedDescriptions.forEach(({ id, tag_position }) => {
        // Keep existing position or set default
        newPositions[id] = tag_position || prev[id] || "START";
      });
      return newPositions;
    });
  }, [savedDescriptions]);

  // Clean up positions when selected descriptions change
  useEffect(() => {
    setSavedDescriptionPositions((prev) => {
      const newPositions: PositionRecord<ContentPosition> = {};
      selectedSavedDescriptions.forEach((id) => {
        // Keep existing position or set default
        newPositions[id] =
          prev[id] ||
          savedDescriptions.find((desc) => desc.id === id)?.position ||
          "INLINE";
      });
      return newPositions;
    });

    setSavedDescriptionHashtagPositions((prev) => {
      const newPositions: PositionRecord<BasicPosition> = {};
      selectedSavedDescriptions.forEach((id) => {
        // Keep existing position or set default
        newPositions[id] =
          prev[id] ||
          savedDescriptions.find((desc) => desc.id === id)?.hashtag_position ||
          "START";
      });
      return newPositions;
    });

    setSavedDescriptionTagPositions((prev) => {
      const newPositions: PositionRecord<BasicPosition> = {};
      selectedSavedDescriptions.forEach((id) => {
        // Keep existing position or set default
        newPositions[id] =
          prev[id] ||
          savedDescriptions.find((desc) => desc.id === id)?.tag_position ||
          "START";
      });
      return newPositions;
    });
  }, [selectedSavedDescriptions, savedDescriptions]);

  const handlePositionChange: PositionHandler<ContentPosition> = (
    descriptionId,
    position
  ) => {
    setSavedDescriptionPositions((prev) => ({
      ...prev,
      [descriptionId]: position,
    }));
    // Close dropdown by removing focus
    (document.activeElement as HTMLElement)?.blur();
  };

  const handleHashtagPositionChange: PositionHandler<BasicPosition> = (
    descriptionId,
    position
  ) => {
    setSavedDescriptionHashtagPositions((prev) => ({
      ...prev,
      [descriptionId]: position,
    }));
    // Close dropdown by removing focus
    (document.activeElement as HTMLElement)?.blur();
  };

  const handleTagPositionChange: PositionHandler<BasicPosition> = (
    descriptionId,
    position
  ) => {
    setSavedDescriptionTagPositions((prev) => ({
      ...prev,
      [descriptionId]: position,
    }));
    // Close dropdown by removing focus
    (document.activeElement as HTMLElement)?.blur();
  };

  const positions: PositionStates = {
    content: savedDescriptionPositions,
    hashtag: savedDescriptionHashtagPositions,
    tag: savedDescriptionTagPositions,
  };

  const handlers: PositionHandlers = {
    content: handlePositionChange,
    hashtag: handleHashtagPositionChange,
    tag: handleTagPositionChange,
  };

  return { positions, handlers };
}
